// JOBE-0044.13: Inspector — right-column component.
//
// Inputs:
//   - `entityUrn`: the selected entity's URN (or null when nothing is
//     selected).
//   - `entity`: the EntityRow for that URN, when the parent already
//     has it in the page cache (the JOBE-0044.4 list response). When
//     the URN is set but the row isn't in cache, the Inspector falls
//     back to fetching `GET /api/entities/{urn}` (single-entity
//     read).
//   - `chips`: optional chip array (predicate + scope chips) for the
//     "Why this row" mode. When omitted, the Inspector reads
//     EntityBrowserContext (the JOBE-0101 host publishes the live
//     state). Direct-prop callers may override for tests or for
//     mounting outside the EntityBrowser host.
//
// Modes (umbrella §R8 mode-swap):
//   - `preview` (default) — entity preview card + envelope + relations
//     (shipped in PR #682).
//   - `why` — "Why this row · filter trace" per-chip match list +
//     query_entities wire fragment. JOBE-0044.13 follow-up.
//   - `cited` — "Cited from Ask" passage list. Stubbed: the tab
//     renders disabled with a "Available once Ask launches" tooltip.
//     Owned by JOBE-0098 + JOBE-0044's Ask phase.
//
// Mode state is local React state; resets to `preview` on row change.
// No URL persistence (per umbrella §R6, selection itself isn't URL-
// persisted either).
//
// Empty state (no selection): quiet placeholder per umbrella §R8 —
// "Select an entity to inspect" in serif italic ink-soft, plus a
// keyboard hint pointing at the first row.

import { useContext, useEffect, useState } from 'react';
import { EntityTypesContext } from '../EntityTypesContext.js';
import { EntityBrowserContext } from '../../entitybrowser/EntityBrowserContext.js';
import { pickPreview } from './previews/registry.js';
import { GenericAttributesPreview } from './previews/GenericAttributesPreview.jsx';
import { WhyThisRow } from './WhyThisRow.jsx';

// useEntityRow fetches a single entity by URN when the parent did not
// pass it in (i.e. the row isn't in the JOBE-0044.4 page cache —
// typical when the user deep-links to a URN). Returns the supplied
// `entity` prop unchanged when present. On mismatch (prop has a URN
// but the entity object is for a different URN), the fetch path wins.
function useEntityRow(entityUrn, entity) {
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (!entityUrn) {
      setFetched(null);
      return undefined;
    }
    if (entity && entity.urn === entityUrn) {
      setFetched(null);
      return undefined;
    }
    const ctrl = new AbortController();
    let alive = true;
    fetch(`/api/entities/${encodeURIComponent(entityUrn)}`, {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive || !r.ok) return;
        const j = await r.json();
        if (!alive) return;
        setFetched(j);
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        // Swallow — the inspector renders a graceful empty state when
        // the single-entity fetch fails; a future spec adds error UI.
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [entityUrn, entity]);

  if (entity && entity.urn === entityUrn) return entity;
  return fetched;
}

function EmptyState() {
  return (
    <div className="library-inspector-empty">
      <p className="library-inspector-empty-prompt">
        Select an entity to inspect
      </p>
      <p className="library-inspector-empty-hint">
        <kbd>↓</kbd> select first row
      </p>
    </div>
  );
}

function EntityPreview({ entity }) {
  const entityTypesCtx = useContext(EntityTypesContext);
  const Preview = pickPreview(entity, entityTypesCtx);
  if (Preview) return <Preview entity={entity} />;
  return <GenericAttributesPreview entity={entity} />;
}

const MODES = [
  { id: 'preview', label: 'Preview', disabled: false },
  { id: 'why', label: 'Why this row', disabled: false },
  {
    id: 'cited',
    label: 'Cited from Ask',
    disabled: true,
    title: 'Coming soon',
  },
];

function ModeStrip({ mode, onModeChange }) {
  return (
    <div
      className="library-inspector-mode-strip"
      role="tablist"
      aria-label="Inspector mode"
    >
      {MODES.map((m) => {
        const active = !m.disabled && m.id === mode;
        return (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`library-inspector-pane-${m.id}`}
            id={`library-inspector-tab-${m.id}`}
            className={[
              'library-inspector-mode-tab',
              active ? 'is-active' : '',
              m.disabled ? 'is-disabled' : '',
            ].filter(Boolean).join(' ')}
            disabled={m.disabled}
            title={m.title || undefined}
            onClick={() => !m.disabled && onModeChange(m.id)}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

// Read chips from EntityBrowserContext when the caller doesn't pass them
// in. The context is optional — tests and surfaces that mount the
// Inspector outside the EntityBrowser host can pass chips directly (or
// omit them entirely, in which case the "Why this row" mode shows the
// scope-only empty state).
function useChips(propChips) {
  const ctx = useContext(EntityBrowserContext);
  if (Array.isArray(propChips)) return propChips;
  if (ctx && Array.isArray(ctx.chips)) return ctx.chips;
  return [];
}

export function Inspector({ entityUrn, entity, chips: propChips }) {
  const resolved = useEntityRow(entityUrn, entity);
  const chips = useChips(propChips);
  const [mode, setMode] = useState('preview');

  // Reset to the default mode whenever the selected URN changes — per
  // the .13 follow-up spec ("resets on row change is fine"). Local
  // state only; no URL persistence.
  useEffect(() => {
    setMode('preview');
  }, [entityUrn]);

  if (!entityUrn) return <EmptyState />;
  if (!resolved) {
    // URN set but row not yet resolved (single-entity fetch in flight,
    // or 4xx/5xx swallowed). Render the empty state quietly — first-
    // paint never flashes a half-rendered card.
    return <EmptyState />;
  }

  return (
    <div className="library-inspector-body">
      <ModeStrip mode={mode} onModeChange={setMode} />
      {mode === 'preview' && (
        <div
          role="tabpanel"
          id="library-inspector-pane-preview"
          aria-labelledby="library-inspector-tab-preview"
        >
          <EntityPreview entity={resolved} />
        </div>
      )}
      {mode === 'why' && (
        <div
          role="tabpanel"
          id="library-inspector-pane-why"
          aria-labelledby="library-inspector-tab-why"
        >
          <WhyThisRow chips={chips} entity={resolved} />
        </div>
      )}
      {/* `cited` is disabled in the strip; no panel renders for it. */}
    </div>
  );
}
